import React from 'react';
import cx from 'classnames';
import useMedia from '@clearscore/hooks.use-media';
import toTitleCase from '@clearscore/normalize.title-case';

import TextLink from './components/text-link';
import TextStrong from './components/text-strong';
import propTypes from './propTypes';
import { types, weights, spacings, tags, presets } from './constants';
import styles from './text.css';

const Text = ({
    children,
    type,
    weight,
    spacing,
    tag: Tag,
    breakpoints,
    isItalic,
    isUppercase,
    isLink,
    isSimpleLink,
    useBaseline,
    dataId,
    ...wrapProps
}) => {
    const typeValue = useMedia(breakpoints, type);
    const typeClass = styles[`isType${toTitleCase(typeValue)}`];
    const weightClass = styles[`isWeight${toTitleCase(weight)}`];
    const spacingClass = styles[`isSpacing${toTitleCase(spacing)}`];
    const isWrapped = isLink || wrapProps.href;

    return (
        <Tag
            className={cx(styles.component, typeClass, weightClass, spacingClass, {
                [styles.isItalic]: isItalic,
                [styles.isUppercase]: isUppercase,
                [styles.useBaseline]: useBaseline,
            })}
            data-qa="text"
            data-id={dataId}
            {...(isWrapped ? {} : wrapProps)}
        >
            {isWrapped ? (
                <TextLink isSimpleLink={isSimpleLink} {...wrapProps}>
                    {children}
                </TextLink>
            ) : (
                children
            )}
        </Tag>
    );
};

Text.propTypes = propTypes;

Text.defaultProps = {
    weight: weights.REGULAR,
    spacing: spacings.TIGHT,
    tag: tags.DIV,
    breakpoints: [],
    isItalic: false,
    isLink: false,
    isSimpleLink: false,
    isUppercase: false,
    useBaseline: false,
};

Text.types = types;
Text.weights = weights;
Text.spacings = spacings;
Text.tags = tags;
Text.presets = presets;

Text.Link = TextLink;
Text.Strong = TextStrong;

/* eslint-disable react/prop-types */
Text.Link1 = ({ tag, ...props }) => <Text {...props} {...presets.LINK} tag={tag || presets.LINK.tag} />;
Text.Link1.displayName = 'Link1';
Text.J1 = ({ tag, ...props }) => <Text {...props} {...presets.J1} tag={tag || presets.J1.tag} />;
Text.J1.displayName = 'J1';
Text.J2 = ({ tag, ...props }) => <Text {...props} {...presets.J2} tag={tag || presets.J2.tag} />;
Text.J2.displayName = 'J2';
Text.J3 = ({ tag, ...props }) => <Text {...props} {...presets.J3} tag={tag || presets.J3.tag} />;
Text.J3.displayName = 'J3';
Text.J4 = ({ tag, ...props }) => <Text {...props} {...presets.J4} tag={tag || presets.J4.tag} />;
Text.J4.displayName = 'J4';
Text.H1 = ({ tag, ...props }) => <Text {...props} {...presets.H1} tag={tag || presets.H1.tag} />;
Text.H1.displayName = 'H1';
Text.H2 = ({ tag, ...props }) => <Text {...props} {...presets.H2} tag={tag || presets.H2.tag} />;
Text.H2.displayName = 'H2';
Text.H3 = ({ tag, ...props }) => <Text {...props} {...presets.H3} tag={tag || presets.H3.tag} />;
Text.H3.displayName = 'H3';
Text.H4 = ({ tag, ...props }) => <Text {...props} {...presets.H4} tag={tag || presets.H4.tag} />;
Text.H4.displayName = 'H4';
Text.H5 = ({ tag, ...props }) => <Text {...props} {...presets.H5} tag={tag || presets.H5.tag} />;
Text.H5.displayName = 'H5';
Text.H6 = ({ tag, ...props }) => <Text {...props} {...presets.H6} tag={tag || presets.H6.tag} />;
Text.H6.displayName = 'H6';
Text.Body1 = ({ tag, ...props }) => <Text {...props} {...presets.BODY1} tag={tag || presets.BODY1.tag} />;
Text.Body1.displayName = 'Body1';
Text.Body2 = ({ tag, ...props }) => <Text {...props} {...presets.BODY2} tag={tag || presets.BODY2.tag} />;
Text.Body2.displayName = 'Body2';
Text.Body3 = ({ tag, ...props }) => <Text {...props} {...presets.BODY3} tag={tag || presets.BODY3.tag} />;
Text.Body3.displayName = 'Body3';
Text.Body4 = ({ tag, ...props }) => <Text {...props} {...presets.BODY4} tag={tag || presets.BODY4.tag} />;
Text.Body4.displayName = 'Body4';
Text.Body5 = ({ tag, ...props }) => <Text {...props} {...presets.BODY5} tag={tag || presets.BODY5.tag} />;
Text.Body5.displayName = 'Body5';
Text.Body6 = ({ tag, ...props }) => <Text {...props} {...presets.BODY6} tag={tag || presets.BODY6.tag} />;
Text.Body6.displayName = 'Body6';
Text.Button1 = ({ tag, ...props }) => <Text {...props} {...presets.BUTTON1} tag={tag || presets.BUTTON1.tag} />;
Text.Button1.displayName = 'Button1';
Text.Button2 = ({ tag, ...props }) => <Text {...props} {...presets.BUTTON2} tag={tag || presets.BUTTON2.tag} />;
Text.Button2.displayName = 'Button2';
Text.Caption = ({ tag, ...props }) => <Text {...props} {...presets.CAPTION} tag={tag || presets.CAPTION.tag} />;
Text.Caption.displayName = 'Caption';
Text.Overline = ({ tag, ...props }) => <Text {...props} {...presets.OVERLINE} tag={tag || presets.OVERLINE.tag} />;
Text.Overline.displayName = 'Overline';
/* eslint-enable react/prop-types */

export default Text;
